import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchPolicies, fetchPoliciesForMultipleUsers } from '../policies/policySlice';
import {
    Box,
    Typography,
    TextField,
    Paper,
    IconButton,
    InputAdornment,
    MenuItem,
    FormControl,
    Select,
    InputLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useResponsiveFonts } from '../../utils/responsiveFonts';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { getPolicyCountsByCategory } from '../../utils/countUtils';
//import { navigateWithScrollReset } from '../../utils/ScrollRestoration';
import MobileBottomNavigation from '../../components/common/MobileBottomNavigation';
import { handleMobileRefresh } from '../../utils/mobileRefresh';
import { useAdminStaffData } from '../../hooks/useAdminStaffData';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { format } from "date-fns";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import Badge from '@mui/material/Badge';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import LinearProgress from '@mui/material/LinearProgress';
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { getAuth } from 'firebase/auth';
import { COMPANIES, POLICY_TYPES } from '../../data/dropdowns';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { firebaseCache } from '../../utils/firebaseCache';

dayjs.extend(customParseFormat);

const MobileCommissionList = () => {
    // Helper to calculate commission dynamically
    const calculatePolicyCommission = (policy) => {
        // If totalCommission is already pre-calculated and stored, use it
        return Math.max(0, parseFloat(policy.totalCommission || 0));
    };

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { policies, loading, initialized } = useSelector((state) => state.policies);
    const { user } = useSelector((state) => state.auth);
    const { userIds, isAdmin, loading: staffLoading } = useAdminStaffData();
    const fonts = useResponsiveFonts();

    const [searchTerm, setSearchTerm] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('motor');
    const [displayedPolicies, setDisplayedPolicies] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Filter state
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [selectedPolicyTypes, setSelectedPolicyTypes] = useState({}); // Object keyed by category for tab-specific filtering
    const [showCompanyOptions, setShowCompanyOptions] = useState(false);
    const [showPolicyTypeOptions, setShowPolicyTypeOptions] = useState(false);
    const [isFilterClosing, setIsFilterClosing] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [showDateRangeCalendar, setShowDateRangeCalendar] = useState(null);

    // Upload dialog state (admin only)
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [uploadCompany, setUploadCompany] = useState('');
    const fileInputRef = useRef(null);

    const itemsPerPage = 10;
    const contentRef = useRef(null);
    const isInitialMount = useRef(true);

    // Refresh handler using shared refresh function
    const handleRefresh = async () => {
        if (isRefreshing) return;

        try {
            await handleMobileRefresh({
                userId: user?.uid,
                dispatch,
                setRefreshing: setIsRefreshing
            });
            console.log('✅ CommissionList data refreshed');
        } catch (error) {
            console.error('❌ Error refreshing CommissionList:', error);
        }
    };

    const categories = ['motor', 'non-motor', 'life'];

    // Fetch Logic adapted from CommissionList
    useEffect(() => {
        // Wait for useAdminStaffData to resolve admin status and staff UIDs
        if (staffLoading) return;

        if (isAdmin && userIds.length > 0) {
            console.log('Admin user (Mobile) - fetching aggregated policies for commissions:', userIds);
            dispatch(fetchPoliciesForMultipleUsers(userIds));
        } else if (user?.uid && !isAdmin) {
            // For regular staff, only fetch if not already loaded or if needed
            if (policies.length === 0) {
                console.log('Staff user (Mobile) - fetching own policies for commissions');
                dispatch(fetchPolicies(user.uid));
            }
        }
    }, [userIds, isAdmin, user?.uid, staffLoading, dispatch, policies.length]);

    // Restore state from navigation state or sessionStorage
    useEffect(() => {
        if (location.state?.fromCategory) {
            console.log('MobileCommissionList: Restoring category from state:', location.state.fromCategory);
            setSelectedCategory(location.state.fromCategory);
        } else {
            const storedCategory = sessionStorage.getItem('mobileCommissionList_selectedCategory');
            const storedSearchTerm = sessionStorage.getItem('mobileCommissionList_searchTerm');
            const storedShowSearch = sessionStorage.getItem('mobileCommissionList_showSearch');

            if (storedCategory) {
                console.log('MobileCommissionList: Restoring state from sessionStorage');
                setSelectedCategory(storedCategory);
                if (storedSearchTerm) setSearchTerm(storedSearchTerm);
                if (storedShowSearch) setShowSearch(storedShowSearch === 'true');

                // Clear storage after restoration
                sessionStorage.removeItem('mobileCommissionList_selectedCategory');
                sessionStorage.removeItem('mobileCommissionList_searchTerm');
                sessionStorage.removeItem('mobileCommissionList_showSearch');
            }
        }
    }, [location.state]);

    // Restore filter state from sessionStorage on mount
    useEffect(() => {
        const savedFilters = sessionStorage.getItem('mobileCommissionList_filterState');
        if (savedFilters) {
            try {
                const filters = JSON.parse(savedFilters);
                setSelectedCompanies(filters.selectedCompanies || []);
                setSelectedPolicyTypes(filters.selectedPolicyTypes || {});
                setShowCompanyOptions(filters.showCompanyOptions || false);
                setShowPolicyTypeOptions(filters.showPolicyTypeOptions || false);
                // Restore date range
                if (filters.dateRange) {
                    setDateRange({
                        start: filters.dateRange.start ? dayjs(filters.dateRange.start) : null,
                        end: filters.dateRange.end ? dayjs(filters.dateRange.end) : null
                    });
                }
            } catch (error) {
                console.error('Error restoring filter state:', error);
            }
        }
        // Delay setting isInitialMount to false to ensure all state updates complete
        setTimeout(() => {
            isInitialMount.current = false;
        }, 100);
    }, []); // Run only on mount

    // Save filter state to sessionStorage whenever filters change (but not on initial mount)
    useEffect(() => {
        // Skip saving on initial mount to avoid overwriting with empty state
        if (isInitialMount.current) {
            return;
        }

        const filterState = {
            selectedCompanies,
            selectedPolicyTypes,
            showCompanyOptions,
            showPolicyTypeOptions,
            dateRange: {
                start: dateRange.start ? dateRange.start.toISOString() : null,
                end: dateRange.end ? dateRange.end.toISOString() : null
            }
        };
        sessionStorage.setItem('mobileCommissionList_filterState', JSON.stringify(filterState));
    }, [selectedCompanies, selectedPolicyTypes, showCompanyOptions, showPolicyTypeOptions, dateRange]);

    // Scroll to top when category or filters change
    useEffect(() => {
        if (!isInitialMount.current && contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, [selectedCategory, searchTerm, selectedCompanies, selectedPolicyTypes, dateRange]);

    // Filter policies based on selected category, search term, and filters
    const filteredPolicies = policies.filter((policy) => {
        // Exclude new leads
        if (policy.status === "New Lead") return false;

        const categoryMatch =
            selectedCategory === 'motor'
                ? policy.policyCategory === 'motor'
                : selectedCategory === 'non-motor'
                    ? policy.policyCategory === 'non-motor'
                    : policy.policyCategoryType === selectedCategory;

        if (!categoryMatch) return false;

        // Company filter
        if (selectedCompanies.length > 0 && !selectedCompanies.includes(policy.policyCompany)) {
            return false;
        }

        // Policy type filter (only for current tab)
        const currentTabPolicyTypes = selectedPolicyTypes[selectedCategory] || [];
        if (currentTabPolicyTypes.length > 0 && !currentTabPolicyTypes.includes(policy.policyType)) {
            return false;
        }

        // Date range filter (on createdAt timestamp)
        if (dateRange.start || dateRange.end) {
            const created = policy.createdAt ? (policy.createdAt.seconds ? dayjs(policy.createdAt.seconds * 1000) : dayjs(policy.createdAt)) : null;
            if (created && created.isValid()) {
                if (dateRange.start && created.isBefore(dateRange.start, 'day')) {
                    return false;
                }
                if (dateRange.end && created.isAfter(dateRange.end, 'day')) {
                    return false;
                }
            } else if (dateRange.start || dateRange.end) {
                // If date range is set but policy has no valid date, exclude it
                return false;
            }
        }

        const searchLower = searchTerm.toLowerCase();
        const hitsBase =
            (policy.name || '').toLowerCase().includes(searchLower) ||
            (policy.mobile || '').includes(searchTerm);

        if (selectedCategory === 'motor') {
            const searchClean = searchLower.replace(/-/g, "");
            const vehicleNumberClean = (policy.vehicleNumber || "")
                .toLowerCase()
                .replace(/-/g, "");
            const hitVehicle = vehicleNumberClean.includes(searchClean);
            return hitsBase || hitVehicle;
        }

        return hitsBase;
    });

    // Calculate counts for each category with cross-tab filtering
    // Company and Date filters apply to all tabs, Policy Type only to current tab
    const categoryCounts = useMemo(() => {
        const counts = { motor: 0, 'non-motor': 0, life: 0 };

        policies.forEach((p) => {
            if (p.status === "New Lead") return;

            // Apply company filter (cross-tab)
            if (selectedCompanies.length > 0 && !selectedCompanies.includes(p.policyCompany)) {
                return;
            }

            // Apply date range filter (cross-tab)
            if (dateRange.start && dateRange.end) {
                const created = p.createdAt ? (p.createdAt.seconds ? dayjs(p.createdAt.seconds * 1000) : dayjs(p.createdAt)) : null;
                if (!created || !created.isValid()) return;

                const start = dateRange.start.startOf('day');
                const end = dateRange.end.endOf('day');

                if (created.isBefore(start) || created.isAfter(end)) return;
            }

            // Determine category
            if (p.policyCategory === 'motor') {
                counts.motor++;
            } else if (p.policyCategory === 'non-motor') {
                counts['non-motor']++;
            } else if (p.policyCategoryType === 'life') {
                counts.life++;
            }
        });

        return counts;
    }, [policies, selectedCompanies, dateRange]);

    // Calculate total commission for filtered policies
    const totalCommission = filteredPolicies.reduce((sum, policy) =>
        sum + calculatePolicyCommission(policy), 0
    );

    // Sort by createdAt (newest first)
    const sortedPolicies = filteredPolicies.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA;
    });

    // Initialize displayed policies when filters change
    useEffect(() => {
        if (!initialized) return;

        if (sortedPolicies.length > 0) {
            setDisplayedPolicies(sortedPolicies);
            setHasMore(false);
        } else {
            setDisplayedPolicies([]);
            setHasMore(false);
        }
    }, [selectedCategory, searchTerm, policies, initialized, selectedCompanies.join(','), JSON.stringify(selectedPolicyTypes), dateRange]);

    // Infinite scroll logic
    const loadMorePolicies = useCallback(() => {
        if (isLoading || !hasMore) return;

        setIsLoading(true);

        setTimeout(() => {
            setDisplayedPolicies(prev => {
                const currentLength = prev.length;
                const currentSortedPolicies = filteredPolicies.sort((a, b) => {
                    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
                    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
                    return dateB - dateA;
                });
                const nextPolicies = currentSortedPolicies.slice(currentLength, currentLength + itemsPerPage);

                if (nextPolicies.length > 0) {
                    setHasMore(currentLength + nextPolicies.length < currentSortedPolicies.length);
                    return [...prev, ...nextPolicies];
                } else {
                    setHasMore(false);
                    return prev;
                }
            });

            setIsLoading(false);
        }, 300);
    }, [isLoading, hasMore, filteredPolicies, itemsPerPage]);

    // Scroll event listener for infinite scroll
    useEffect(() => {
        const handleScroll = () => {
            if (!contentRef.current) return;

            const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
            const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

            if (isNearBottom && hasMore && !isLoading) {
                loadMorePolicies();
            }
        };

        const contentElement = contentRef.current;
        if (contentElement) {
            contentElement.addEventListener('scroll', handleScroll, { passive: true });
            return () => contentElement.removeEventListener('scroll', handleScroll);
        }
    }, [hasMore, isLoading, loadMorePolicies]);

    // Show loading state until data is actually loaded
    if (loading || !initialized) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                <Typography>Loading commissions...</Typography>
            </Box>
        );
    }

    const handlePolicyClick = (policy) => {
        // Store current states in sessionStorage for swipe back support
        sessionStorage.setItem('mobileCommissionList_selectedCategory', selectedCategory);
        sessionStorage.setItem('mobileCommissionList_searchTerm', searchTerm);
        sessionStorage.setItem('mobileCommissionList_showSearch', showSearch.toString()); // Convert boolean to string

        const navigationState = { policy, fromScreen: 'commissions', fromCategory: selectedCategory };
        navigate(`/commission/${policy.id}`, { state: navigationState });
    };

    const toNumber = (val) => {
        if (!val) return 0;
        const cleaned = String(val).replace(/[^\d.-]/g, '');
        const num = Number(cleaned);
        return isNaN(num) ? 0 : num;
    };

    // Filter handlers
    const handleFilterButtonClick = (event) => {
        if (filterAnchorEl) {
            handleFilterClose();
        } else {
            setFilterAnchorEl(event.currentTarget);
            setIsFilterClosing(false);
        }
    };

    const handleFilterClose = () => {
        setIsFilterClosing(true);
        setTimeout(() => {
            setFilterAnchorEl(null);
            setIsFilterClosing(false);
        }, 200);
    };

    const handleCompanyToggle = (company) => {
        setSelectedCompanies((prev) =>
            prev.includes(company)
                ? prev.filter((c) => c !== company)
                : [...prev, company]
        );
    };

    const handlePolicyTypeToggle = (policyType) => {
        setSelectedPolicyTypes((prev) => {
            const currentTypes = prev[selectedCategory] || [];
            const updatedTypes = currentTypes.includes(policyType)
                ? currentTypes.filter((t) => t !== policyType)
                : [...currentTypes, policyType];
            return { ...prev, [selectedCategory]: updatedTypes };
        });
    };

    const handleCompanyCheckboxChange = (checked) => {
        setShowCompanyOptions(checked);
        if (!checked) {
            setSelectedCompanies([]);
        }
    };

    const handlePolicyTypeCheckboxChange = (checked) => {
        setShowPolicyTypeOptions(checked);
        if (!checked) {
            setSelectedPolicyTypes({});
        }
    };

    const clearFilters = () => {
        setSelectedCompanies([]);
        setSelectedPolicyTypes({});
        setShowCompanyOptions(false);
        setShowPolicyTypeOptions(false);
        setDateRange({ start: null, end: null });
        setShowDateRangeCalendar(null);
    };

    const handleDateRangeCalendarChange = (date) => {
        const selectedDay = dayjs(date);

        if (showDateRangeCalendar === 'start') {
            setDateRange({
                start: selectedDay,
                end: dateRange.end && selectedDay.isAfter(dateRange.end) ? null : dateRange.end
            });
            if (!dateRange.end) {
                setShowDateRangeCalendar('end');
            }
        } else if (showDateRangeCalendar === 'end') {
            if (selectedDay.isBefore(dateRange.start, 'day')) {
                return;
            }
            setDateRange((prev) => ({
                start: prev.start,
                end: selectedDay
            }));
            setShowDateRangeCalendar(null);
        }
    };

    const getCalendarConfig = () => {
        try {
            const testDate = new Date();
            const testLocale = testDate.toLocaleDateString('en');
            return {
                calendarType: 'gregory',
                locale: 'en',
                maxDetail: 'month',
                minDetail: 'month'
            };
        } catch (error) {
            console.warn('Calendar locale detection failed, using fallback:', error);
            return {
                calendarType: 'gregory',
                locale: undefined,
                maxDetail: 'month',
                minDetail: 'month'
            };
        }
    };

    const dateRangeActive = Boolean(dateRange.start && dateRange.end);
    const activeFilterCount = selectedCompanies.length + ((selectedPolicyTypes[selectedCategory] || []).length) + (dateRangeActive ? 1 : 0);

    // Upload handlers (admin only)
    const handleUploadClick = () => {
        setUploadDialogOpen(true);
        setUploadFile(null);
        setUploadResult(null);
        setUploadError(null);
        setUploadCompany('');
    };

    const handleUploadClose = () => {
        if (uploadResult?.success) {
            console.log("🔄 Commission upload successful, refreshing data...");
            // Invalidate cache (both list and individual policy caches)
            firebaseCache.markForInvalidation('policy');

            // Refresh data to show new commissions (force server fetch)
            if (isAdmin && userIds.length > 0) {
                dispatch(fetchPoliciesForMultipleUsers({ userIds, forceRefresh: true }));
            } else if (user?.uid) {
                dispatch(fetchPolicies({ agentId: user.uid, forceRefresh: true }));
            }
        }
        setUploadDialogOpen(false);
        setUploadFile(null);
        setUploadResult(null);
        setUploadError(null);
        setUploadCompany('');
    };

    const handleFileSelect = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            const ext = file.name.split('.').pop().toLowerCase();
            const allowedPdf = ['pdf'];
            const allowedExcel = ['xlsx', 'xls'];

            if (uploadCompany === 'GODIGIT') {
                if (allowedExcel.includes(ext)) {
                    setUploadFile(file);
                    setUploadError(null);
                } else {
                    setUploadError('Please select an Excel file (.xlsx or .xls)');
                }
            } else {
                if (allowedPdf.includes(ext)) {
                    setUploadFile(file);
                    setUploadError(null);
                } else {
                    setUploadError('Please select a PDF file');
                }
            }
        }
    };

    const handleUploadSubmit = async () => {
        if (!uploadFile) return;

        setUploadLoading(true);
        setUploadError(null);
        setUploadResult(null);

        try {
            const auth = getAuth();
            const token = await auth.currentUser?.getIdToken();

            if (!token) {
                throw new Error('Not authenticated');
            }

            if (!uploadCompany) {
                throw new Error('Please select a company');
            }

            const formData = new FormData();
            formData.append('pdf', uploadFile);
            formData.append('company', uploadCompany);

            const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5001';
            const response = await fetch(`${backendUrl}/api/commission/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || result.error || 'Upload failed');
            }

            setUploadResult(result);

            // Refresh policies to show updated commissions (force server refresh)
            if (result.successfullyUpdated > 0) {
                firebaseCache.markForInvalidation('policy');
                if (isAdmin && userIds.length > 0) {
                    dispatch(fetchPoliciesForMultipleUsers({ userIds, forceRefresh: true }));
                } else if (user?.uid) {
                    dispatch(fetchPolicies({ agentId: user.uid, forceRefresh: true }));
                }
            }
        } catch (error) {
            console.error('Upload error:', error);
            setUploadError(error.message || 'Failed to upload commission PDF');
        } finally {
            setUploadLoading(false);
        }
    };

    return (
        <Box className="mobile-layout mobile-content-container" sx={{
            width: '100vw',
            minHeight: '100vh',
            overflowX: 'hidden',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
        }}>
            {/* Header */}
            <Box sx={{
                backgroundColor: '#0063F7',
                color: 'white',
                px: { xs: 2, sm: 3, md: 4 },
                py: 1,
                height: 70,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1002 // Highest zIndex
            }}>
                <Typography sx={{
                    fontSize: fonts.title.xs,
                    fontWeight: 600
                }}>
                    Commission
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isAdmin && (
                        <IconButton
                            onClick={handleUploadClick}
                            sx={{ color: 'white' }}
                        >
                            <UploadFileIcon />
                        </IconButton>
                    )}
                    <IconButton
                        onClick={() => setShowSearch(!showSearch)}
                        sx={{ color: 'white' }}
                    >
                        <SearchIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Search Bar */}
            {showSearch && (
                <Box sx={{
                    backgroundColor: 'white',
                    px: { xs: 2, sm: 3, md: 4 },
                    py: 1.5,
                    borderBottom: '1px solid #e0e0e0',
                    position: 'fixed',
                    top: 70,
                    left: 0,
                    right: 0,
                    zIndex: 1001,
                    height: '70px',
                    boxSizing: 'border-box'
                }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search by name, number or vehicle number"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#757575' }} />
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
            )}

            {/* Summary Card */}
            <Box sx={{
                position: 'fixed',
                top: showSearch ? 140 : 70,
                left: 0,
                right: 0,
                zIndex: 999,
                backgroundColor: '#f5f5f5',
                px: 2,
                pt: 2, // Added more padding to create clear gap
                pb: 0,
                transition: 'top 0.3s ease'
            }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 0 }}>
                    {/* Total Commission Card */}
                    <Paper sx={{
                        flex: 1,
                        p: 2,
                        borderRadius: '12px',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                        <Typography sx={{ color: '#166534', fontSize: '13px', fontWeight: 500, mb: 0.5 }}>
                            Total Commission
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#166534', width: '100%' }}>
                            <CurrencyRupeeIcon sx={{ fontSize: 24 }} />
                            <Typography sx={{
                                fontSize: '32px !important',
                                fontWeight: 900,
                                lineHeight: 1,
                                '&.MuiTypography-root': { fontSize: '26px !important' }
                            }}>
                                {totalCommission.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                        </Box>
                    </Paper>

                    {/* Total Premium Card */}
                    <Paper sx={{
                        flex: 1,
                        p: 2,
                        borderRadius: '12px',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                        <Typography sx={{ color: '#F04438', fontSize: '13px', fontWeight: 500, mb: 0.5 }}>
                            Total Premium
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F04438', width: '100%' }}>
                            <CurrencyRupeeIcon sx={{ fontSize: 24 }} />
                            <Typography sx={{
                                fontSize: '32px !important',
                                fontWeight: 900,
                                lineHeight: 1,
                                '&.MuiTypography-root': { fontSize: '26px !important' }
                            }}>
                                {filteredPolicies.reduce((sum, p) => sum + (parseFloat(p.netPremium) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>

                {/* Category Tabs */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: 1,
                    mb: 0.5
                }}>
                    {categories.map((category) => (
                        <Box
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            sx={{
                                flex: 1,
                                py: 0.8,
                                borderRadius: '20px',
                                backgroundColor: selectedCategory === category ? '#E3F2FD' : 'white',
                                border: `1px solid ${selectedCategory === category ? '#0063F7' : '#e0e0e0'}`,
                                cursor: 'pointer',
                                textAlign: 'center'
                            }}
                        >
                            <Typography sx={{
                                fontSize: '20px !important',
                                fontWeight: 800,
                                lineHeight: 1,
                                color: selectedCategory === category ? '#0063F7' : '#666',
                                textTransform: 'capitalize',
                                '&.MuiTypography-root': { fontSize: '14px !important' }
                            }}>
                                {category === 'motor' ? `Motor (${categoryCounts.motor || 0})` :
                                    category === 'life' ? `Life (${categoryCounts.life || 0})` :
                                        `Non-Motor (${categoryCounts['non-motor'] || 0})`}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Policy List Grouped by Date */}
            <Box
                sx={{
                    px: 2,
                    pt: 1,
                    pb: 10,
                    mt: showSearch ? '311px' : '241px',
                    transition: 'margin-top 0.3s ease',
                    overflowX: 'hidden'
                }}>
                {displayedPolicies.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography sx={{ color: '#666', mb: 1 }}>
                            No commissions found
                        </Typography>
                    </Box>
                ) : (
                    Object.entries(
                        displayedPolicies.reduce((groups, policy) => {
                            const date = policy.createdAt ? new Date(policy.createdAt) : new Date();
                            const dateKey = format(date, 'EEEE d MMMM'); // e.g., Monday 3 January
                            if (!groups[dateKey]) groups[dateKey] = [];
                            groups[dateKey].push(policy);
                            return groups;
                        }, {})
                    ).map(([dateLabel, policies]) => (
                        <Box key={dateLabel} sx={{ mb: 3 }}>
                            <Typography sx={{
                                fontSize: '14px',
                                color: '#757575',
                                mb: 1.5,
                                px: 0.5
                            }}>
                                {dateLabel}
                            </Typography>
                            {policies.map((policy) => (
                                <Paper
                                    key={policy.id}
                                    onClick={() => handlePolicyClick(policy)}
                                    sx={{
                                        p: 2,
                                        mb: 1.5,
                                        borderRadius: '12px',
                                        backgroundColor: 'white',
                                        boxShadow: 'none',
                                        border: '1px solid #f0f0f0',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {/* Motor: Show Company at top separate. Non-Motor: Show inside left col */}
                                    {selectedCategory === 'motor' && (
                                        <Box sx={{ mb: 1 }}>
                                            <Typography sx={{
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: '#0063F7',
                                            }}>
                                                {policy.policyCompany || 'Insurance Company'}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                            {selectedCategory === 'motor' ? (
                                                /* Motor Layout: Icon+MakeModel, then VehicleNumber */
                                                <>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                                                        <DirectionsCarIcon sx={{ fontSize: 18, color: '#333' }} />
                                                        <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#212121' }}>
                                                            {(policy.vehicleMake && policy.vehicleModel ?
                                                                (policy.vehicleMake + ' ' + policy.vehicleModel).length > 20 ?
                                                                    (policy.vehicleMake + ' ' + policy.vehicleModel).substring(0, 20) + '...' :
                                                                    (policy.vehicleMake + ' ' + policy.vehicleModel)
                                                                : (policy.vehicleMake || policy.vehicleModel || 'Vehicle'))}
                                                        </Typography>
                                                    </Box>
                                                    <Typography sx={{ fontSize: '14px', color: '#757575' }}>
                                                        {policy.vehicleNumber || 'N/A'}
                                                    </Typography>
                                                </>
                                            ) : (
                                                /* Non-Motor/Life Layout: Company, Policy Type, Net Premium */
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 600,
                                                        color: '#0063F7', // Blue
                                                        lineHeight: 1.2
                                                    }}>
                                                        {policy.policyCompany || '-'}
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        color: '#212121', // Black
                                                        lineHeight: 1.2
                                                    }}>
                                                        {policy.policyType || '-'}
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        color: '#757575', // Grey
                                                        lineHeight: 1.2,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5
                                                    }}>
                                                        ₹ {parseFloat(policy.netPremium || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        <Typography sx={{
                                            fontSize: '16px',
                                            fontWeight: 500,
                                            color: calculatePolicyCommission(policy) >= 0 ? '#166534' : '#E11D48'
                                        }}>
                                            ₹ {calculatePolicyCommission(policy).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Typography>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    ))
                )}
                {/* Loading Indicator */}
                {isLoading && (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography sx={{ fontSize: '14px', color: '#757575' }}>
                            Loading more...
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Filter Box */}
            {Boolean(filterAnchorEl) && (
                <Box
                    data-filter-box="true"
                    sx={{
                        position: 'fixed',
                        bottom: 170,
                        right: '2vw',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                        zIndex: 1300,
                        width: '96vw',
                        maxWidth: 500,
                        maxHeight: 'calc(100vh - 250px)',
                        overflowY: 'auto',
                        p: 1.5,
                        transformOrigin: 'bottom right',
                        animation: isFilterClosing
                            ? 'collapseToFab 0.2s ease-in forwards'
                            : 'expandFromFab 0.3s ease-out',
                        '@keyframes expandFromFab': {
                            '0%': {
                                opacity: 0,
                                transform: 'scale(0.3)',
                                transformOrigin: 'bottom right'
                            },
                            '100%': {
                                opacity: 1,
                                transform: 'scale(1)',
                                transformOrigin: 'bottom right'
                            }
                        },
                        '@keyframes collapseToFab': {
                            '0%': {
                                opacity: 1,
                                transform: 'scale(1)',
                                transformOrigin: 'bottom right'
                            },
                            '100%': {
                                opacity: 0,
                                transform: 'scale(0.3)',
                                transformOrigin: 'bottom right'
                            }
                        }
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Stack spacing={1.5}>
                        {/* Company Filter - Only show if user has multiple companies */}
                        {(user?.hasMultipleCompanies !== false) && (
                            <>
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={showCompanyOptions}
                                                    onChange={(e) => handleCompanyCheckboxChange(e.target.checked)}
                                                />
                                            }
                                            label={<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Company</Typography>}
                                        />
                                    </Box>
                                    {showCompanyOptions && (
                                        <Box sx={{ mt: 0.75, ml: 2 }}>
                                            <Box sx={{ maxHeight: 180, overflowY: 'auto', pr: 1 }}>
                                                <Box sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 1fr',
                                                    gap: 0.5
                                                }}>
                                                    {(() => {
                                                        const companies = selectedCategory === 'motor'
                                                            ? COMPANIES.motor
                                                            : selectedCategory === 'life'
                                                                ? COMPANIES.life
                                                                : COMPANIES.non_motor;

                                                        return companies.map((company) => (
                                                            <FormControlLabel
                                                                key={company}
                                                                control={
                                                                    <Checkbox
                                                                        checked={selectedCompanies.includes(company)}
                                                                        onChange={() => handleCompanyToggle(company)}
                                                                        size="small"
                                                                        sx={{ p: 0.5 }}
                                                                    />
                                                                }
                                                                label={company}
                                                                sx={{
                                                                    margin: 0,
                                                                    '& .MuiFormControlLabel-label': {
                                                                        fontSize: 12,
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        maxWidth: '100px'
                                                                    }
                                                                }}
                                                            />
                                                        ));
                                                    })()}
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>

                                <Divider sx={{ my: 0.75 }} />
                            </>
                        )}

                        {/* Policy Type Filter */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={showPolicyTypeOptions}
                                            onChange={(e) => handlePolicyTypeCheckboxChange(e.target.checked)}
                                        />
                                    }
                                    label={<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Policy Type</Typography>}
                                />
                            </Box>
                            {showPolicyTypeOptions && (
                                <Box sx={{ mt: 0.75, ml: 2 }}>
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: 0.5
                                    }}>
                                        {(() => {
                                            const policyTypes = selectedCategory === 'motor'
                                                ? POLICY_TYPES.motor
                                                : selectedCategory === 'life'
                                                    ? POLICY_TYPES.life
                                                    : POLICY_TYPES.health;

                                            return policyTypes.map((policyType) => (
                                                <FormControlLabel
                                                    key={policyType}
                                                    control={
                                                        <Checkbox
                                                            checked={(selectedPolicyTypes[selectedCategory] || []).includes(policyType)}
                                                            onChange={() => handlePolicyTypeToggle(policyType)}
                                                            size="small"
                                                            sx={{ p: 0.5 }}
                                                        />
                                                    }
                                                    label={policyType}
                                                    sx={{
                                                        margin: 0,
                                                        '& .MuiFormControlLabel-label': {
                                                            fontSize: 12,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            maxWidth: '100px'
                                                        }
                                                    }}
                                                />
                                            ));
                                        })()}
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        <Divider sx={{ my: 0.75 }} />

                        {/* Date Range Filter */}
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Date Range</Typography>
                            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                <Button
                                    variant={showDateRangeCalendar === 'start' ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => setShowDateRangeCalendar(showDateRangeCalendar === 'start' ? null : 'start')}
                                    sx={{
                                        flex: 1,
                                        fontSize: '0.75rem',
                                        '&:hover': {
                                            backgroundColor: showDateRangeCalendar === 'start' ? '#0056d6' : '#E0EBFF',
                                            borderColor: '#E0EBFF'
                                        }
                                    }}
                                >
                                    {dateRange.start ? dateRange.start.format('DD MMM') : 'START'}
                                </Button>
                                <Button
                                    variant={showDateRangeCalendar === 'end' ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => {
                                        if (dateRange.start) {
                                            setShowDateRangeCalendar(showDateRangeCalendar === 'end' ? null : 'end');
                                        }
                                    }}
                                    sx={{
                                        flex: 1,
                                        fontSize: '0.75rem',
                                        '&:hover': {
                                            backgroundColor: showDateRangeCalendar === 'end' ? '#0056d6' : '#E0EBFF',
                                            borderColor: '#E0EBFF'
                                        }
                                    }}
                                    disabled={!Boolean(dateRange.start)}
                                >
                                    {dateRange.end ? dateRange.end.format('DD MMM') : 'END'}
                                </Button>
                            </Stack>
                            {showDateRangeCalendar && (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        zIndex: 1300,
                                        boxShadow: 3,
                                        borderRadius: 2,
                                        bgcolor: 'background.paper',
                                        width: '100%',
                                        overflow: 'hidden',
                                        padding: '6px',
                                        mt: 1,
                                        '& .react-calendar': {
                                            width: '100%',
                                            maxWidth: '100%',
                                            minHeight: '160px',
                                            border: 'none',
                                            fontFamily: 'Roboto',
                                            '&__navigation': {
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '0 2px',
                                                margin: '0 0 4px 0',
                                                '&__label': {
                                                    minWidth: '110px',
                                                    textAlign: 'center',
                                                    flexGrow: 0,
                                                    fontWeight: '600 !important',
                                                    margin: 0,
                                                    padding: '2px 0',
                                                    fontSize: '12px',
                                                    lineHeight: '1.1'
                                                },
                                                '&__arrow': {
                                                    minWidth: '24px',
                                                    padding: '2px 0px',
                                                    margin: 0,
                                                    fontSize: '16px'
                                                }
                                            },
                                            '&__month-view__weekdays': {
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                padding: '0 2px',
                                                marginBottom: '1px',
                                                '&__weekday': {
                                                    width: '26px',
                                                    padding: '1px 0',
                                                    textAlign: 'center',
                                                    fontSize: '9px',
                                                    fontWeight: '600 !important',
                                                    textTransform: 'uppercase',
                                                    color: '#666 !important',
                                                    backgroundColor: 'transparent !important',
                                                    border: 'none !important',
                                                    '& abbr': {
                                                        textDecoration: 'none',
                                                        display: 'inline-block',
                                                        width: '100%',
                                                        textAlign: 'center'
                                                    }
                                                }
                                            },
                                            '&__month-view__days': {
                                                display: 'grid !important',
                                                gridTemplateColumns: 'repeat(7, 1fr)',
                                                gap: '1px',
                                                padding: '0 2px',
                                                '&__day': {
                                                    width: '26px',
                                                    height: '26px',
                                                    margin: '0 auto',
                                                    fontWeight: '600 !important',
                                                    padding: '0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '10px',
                                                    '&--neighboringMonth': {
                                                        opacity: 0.5
                                                    },
                                                    '&--weekend': {
                                                        color: '#212121 !important'
                                                    },
                                                    '&--now': {
                                                        fontWeight: 'bold',
                                                        color: '#004DC2 !important',
                                                        backgroundColor: 'transparent !important',
                                                        '&:enabled:hover': {
                                                            backgroundColor: '#E0EBFF !important'
                                                        }
                                                    }
                                                }
                                            }
                                        },
                                        '& .react-calendar__tile': {
                                            fontFamily: 'Roboto',
                                            fontWeight: 500,
                                            fontSize: '13px',
                                            color: '#212121 !important',
                                            transition: 'all 0.2s ease',
                                            '&:enabled:hover': {
                                                backgroundColor: '#E0EBFF !important',
                                                color: '#212121 !important'
                                            },
                                            '&--active, &--active:enabled, &--active:enabled:hover': {
                                                backgroundColor: '#004DC2 !important',
                                                color: '#fff !important',
                                                borderRadius: '50%',
                                                border: 'none',
                                                boxShadow: 'none',
                                                outline: 'none'
                                            }
                                        },
                                        "& .react-calendar__tile--active, & .react-calendar__tile--active:enabled": {
                                            backgroundColor: '#004DC2 !important',
                                            color: '#fff !important',
                                        },
                                        "& .react-calendar__month-view__days__day--weekend": {
                                            color: '#212121 !important',
                                        },
                                        "& .react-calendar__tile:not(.react-calendar__tile--active):enabled:hover": {
                                            backgroundColor: '#E0EBFF !important',
                                        },
                                        "& .react-calendar__navigation button": {
                                            color: '#333 !important',
                                            background: 'none !important',
                                            fontWeight: '600 !important',
                                            border: 'none !important',
                                            outline: 'none !important',
                                            '&:hover': {
                                                backgroundColor: '#E0EBFF !important',
                                                color: '#333 !important',
                                            },
                                            '&:focus': {
                                                backgroundColor: 'transparent !important',
                                                outline: 'none !important',
                                                boxShadow: 'none !important',
                                            },
                                            '&:active': {
                                                backgroundColor: 'transparent !important',
                                            },
                                        },
                                        "& .react-calendar__navigation__label": {
                                            color: '#333 !important',
                                            background: 'transparent !important',
                                            fontWeight: '600 !important',
                                            '&:hover': {
                                                backgroundColor: 'transparent !important',
                                            },
                                            '&:focus': {
                                                backgroundColor: 'transparent !important',
                                                outline: 'none !important',
                                            },
                                        },
                                    }}
                                >
                                    {(() => {
                                        try {
                                            return (
                                                <Calendar
                                                    value={dateRange.start?.toDate() || dateRange.end?.toDate()}
                                                    onChange={handleDateRangeCalendarChange}
                                                    {...getCalendarConfig()}
                                                />
                                            );
                                        } catch (error) {
                                            console.error('Calendar render error:', error);
                                            return <Typography sx={{ p: 2, color: 'error.main' }}>Calendar error</Typography>;
                                        }
                                    })()}
                                </Box>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5 }}>
                                {showDateRangeCalendar ? `Select ${showDateRangeCalendar} date` : 'Click to select date range'}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 0.75 }} />

                        {/* Action Buttons */}
                        <Stack direction="row" spacing={1.5} sx={{ pt: 0.5 }}>
                            <Button
                                onClick={clearFilters}
                                size="small"
                                sx={{
                                    flex: 1,
                                    '&:hover': {
                                        backgroundColor: '#F5F5F5'
                                    }
                                }}
                            >
                                Clear filters
                            </Button>
                            <Button
                                onClick={handleFilterClose}
                                size="small"
                                variant="contained"
                                sx={{
                                    flex: 1
                                }}
                            >
                                Done
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            )}

            {/* Filter FAB Button */}
            <Box
                onClick={handleFilterButtonClick}
                sx={{
                    position: 'fixed',
                    bottom: 100,
                    right: { xs: 16, sm: 24 },
                    width: { xs: 56, sm: 64 },
                    height: { xs: 56, sm: 64 },
                    backgroundColor: '#0063F7',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(0, 99, 247, 0.3)',
                    zIndex: 1001,
                    '&:active': {
                        transform: 'scale(0.95)',
                    },
                    '&:hover': {
                        backgroundColor: '#0051d4',
                        boxShadow: '0 6px 20px rgba(0, 99, 247, 0.4)',
                    },
                }}
            >
                <Badge
                    color="primary"
                    badgeContent={activeFilterCount}
                    invisible={activeFilterCount === 0}
                >
                    <FilterAltOutlinedIcon sx={{ fontSize: 24, color: 'white' }} />
                </Badge>
            </Box>

            {/* Bottom Navigation */}
            <MobileBottomNavigation isAdmin={isAdmin} contentRef={contentRef} />

            {/* Upload Dialog (Admin Only) */}
            <Dialog
                open={uploadDialogOpen}
                onClose={handleUploadClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 2, m: 2 } }}
            >
                <DialogTitle sx={{ m: 0, p: 2, textAlign: "left !important", position: 'relative' }}>
                    <Typography
                        sx={{
                            fontFamily: "Roboto Slab !important",
                            fontWeight: "500 !important",
                            fontSize: "24px !important",
                            lineHeight: "32px !important",
                            color: "#000000 !important",
                            textAlign: "center !important"
                        }}
                    >
                        {uploadResult ? "Update Result" : "Upload Commission"}
                    </Typography>
                    <IconButton
                        aria-label="Close"
                        onClick={handleUploadClose}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ textAlign: "center", pt: 1, px: 2 }}>
                    {!uploadResult && !uploadLoading && (
                        <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="mobile-upload-company-label">Select Company</InputLabel>
                                <Select
                                    labelId="mobile-upload-company-label"
                                    value={uploadCompany}
                                    label="Select Company"
                                    onChange={(e) => {
                                        setUploadCompany(e.target.value);
                                        setUploadFile(null);
                                        setUploadError(null);
                                    }}
                                >
                                    <MenuItem value="NEW_INDIA">New India Assurance (PDF)</MenuItem>
                                    <MenuItem value="LIC">LIC (PDF)</MenuItem>
                                    <MenuItem value="STAR">Star (PDF)</MenuItem>
                                    <MenuItem value="GODIGIT">GoDigit (Excel)</MenuItem>
                                </Select>
                            </FormControl>

                            <Box
                                sx={{
                                    border: "2px dashed #ccc",
                                    borderRadius: "4px",
                                    p: 3,
                                    backgroundColor: "#FAFAFA",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: 150,
                                    cursor: 'pointer',
                                    '&:active': { backgroundColor: '#F0F5FF' }
                                }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <PictureAsPdfIcon sx={{ fontSize: 40, color: "#757575", mb: 1 }} />
                                <Typography
                                    sx={{
                                        fontFamily: "Roboto",
                                        fontWeight: 400,
                                        fontSize: "18px",
                                        lineHeight: "24px",
                                        mb: 1,
                                        textAlign: 'center'
                                    }}
                                >
                                    Tap to select or upload file
                                </Typography>
                                <Button
                                    variant="text"
                                    disableRipple
                                    sx={{
                                        color: "#0063F7 !important",
                                        textTransform: "none",
                                        fontSize: "16px",
                                        fontWeight: 500,
                                        backgroundColor: 'transparent !important',
                                        '&:hover': { backgroundColor: 'transparent !important' },
                                        '&:active': { backgroundColor: 'transparent !important' },
                                        pointerEvents: 'none'
                                    }}
                                >
                                    Choose files
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={uploadCompany === 'GODIGIT' ? '.xlsx,.xls' : '.pdf'}
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                            </Box>
                            {uploadFile && (
                                <Typography sx={{ fontSize: "14px", color: "#666", textAlign: "center", wordBreak: 'break-all' }}>
                                    Selected: {uploadFile.name}
                                </Typography>
                            )}
                            {uploadError && (
                                <Alert severity="error" sx={{ mt: 1, fontSize: '13px' }}>{uploadError}</Alert>
                            )}
                        </Box>
                    )}

                    {uploadLoading && (
                        <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <Typography variant="subtitle1" fontWeight={500}>Extracting Data...</Typography>
                            <Box sx={{ width: '100%', px: 2 }}>
                                <LinearProgress sx={{ height: 8, borderRadius: 4, backgroundColor: "#E5E7EB", "& .MuiLinearProgress-bar": { backgroundColor: "#0063F7" } }} />
                            </Box>
                            <Typography variant="caption" color="text.secondary">This may take a few moments</Typography>
                        </Box>
                    )}

                    {uploadResult && (
                        <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <CheckCircleIcon sx={{ fontSize: 50, color: '#166534' }} />
                            <Typography
                                sx={{
                                    fontFamily: 'Roboto Slab',
                                    fontWeight: 500,
                                    fontSize: '20px',
                                    color: '#166534',
                                    textAlign: 'center'
                                }}
                            >
                                Hurray! Updated successfully
                            </Typography>

                            <Paper variant="outlined" sx={{ p: 2, width: '100%', backgroundColor: '#F0FDF4', borderColor: '#BBF7D0' }}>
                                <Stack spacing={1} sx={{ textAlign: 'left' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" fontWeight={500}>Total Extracted:</Typography>
                                        <Typography variant="body2" fontWeight={600}>{uploadResult.totalExtracted || 0}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" fontWeight={500} color="#166534">Successfully Updated:</Typography>
                                        <Typography variant="body2" fontWeight={600} color="#166534">{uploadResult.successfullyUpdated || 0}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" fontWeight={500} color="#92400E">Skipped:</Typography>
                                        <Typography variant="body2" fontWeight={600} color="#92400E">{uploadResult.skipped?.length || 0}</Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
                {!uploadLoading && (
                    <DialogActions sx={{ p: 2, gap: 1, justifyContent: uploadResult ? 'center' : 'flex-end' }}>
                        {!uploadResult ? (
                            <>
                                <Button
                                    onClick={handleUploadClose}
                                    fullWidth
                                    sx={{
                                        height: 48,
                                        borderRadius: '6px',
                                        borderColor: "#B00020",
                                        color: "#B00020",
                                        fontSize: '16px',
                                        textTransform: 'none'
                                    }}
                                    variant="outlined"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleUploadSubmit}
                                    disabled={!uploadFile}
                                    variant="contained"
                                    fullWidth
                                    sx={{
                                        height: 48,
                                        borderRadius: '6px',
                                        backgroundColor: '#0063F7 !important',
                                        fontSize: '16px',
                                        textTransform: 'none',
                                        '&:disabled': { backgroundColor: '#E0E0E0 !important', color: '#9E9E9E !important' }
                                    }}
                                >
                                    Submit
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    onClick={handleUploadClose}
                                    variant="outlined"
                                    sx={{
                                        flex: 1,
                                        height: "40px",
                                        borderRadius: "6px",
                                        fontSize: "16px",
                                        borderColor: "#B00020",
                                        color: "#B00020",
                                        textTransform: "none",
                                        fontWeight: 500,
                                        "&:hover": {
                                            backgroundColor: "white",
                                            borderColor: "#B00020",
                                        }
                                    }}
                                >
                                    Close
                                </Button>
                                <Button
                                    onClick={handleUploadClose}
                                    variant="contained"
                                    sx={{
                                        flex: 1,
                                        height: "40px",
                                        borderRadius: "6px",
                                        fontSize: "16px",
                                        backgroundColor: "#0063F7 !important",
                                        color: "white !important",
                                        textTransform: "none",
                                        fontWeight: 500,
                                        boxShadow: "none"
                                    }}
                                >
                                    Done
                                </Button>
                            </>
                        )}
                    </DialogActions>
                )}
            </Dialog>
        </Box>
    );
};

export default MobileCommissionList;
